/**
 * FlowCanvas - Reusable React Flow component
 * 
 * Core component that provides a generic flow canvas that can be used
 * for visualizing different types of data (dialogs, objects, templates, logic flows)
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Connection,
  Node,
  Edge,
  useReactFlow,
  ReactFlowProvider,
  OnConnectEnd,
  NodeChange,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FlowCanvasProps } from './types';
import { NodeDetailsPanel } from '../logic/NodeDetailsPanel';
import { hasNodeChanges, hasEdgeChanges, validateHandleConnection } from './utils';
import { useToast } from '@/hooks/use-toast';
import { EdgeStyleContextMenu } from './EdgeStyleContextMenu';
import { HelperLines } from './nodes/node-controls/HelperLines';
import { getHelperLines } from './utils/helperLines';
import { useUndoRedo } from '@/hooks/useUndoRedo';
import { Undo2, Redo2 } from 'lucide-react';

function FlowCanvasContent({
  initialNodes,
  initialEdges,
  nodeTypes,
  edgeTypes,
  onSave,
  onCancel,
  onNodesChange: onNodesChangeProp,
  onEdgesChange: onEdgesChangeProp,
  onNodeClick: onNodeClickProp,
  onEdgeClick: onEdgeClickProp,
  onNodeDoubleClick,
  onEdgeDoubleClick,
  onPaneClick: onPaneClickProp,
  onPaneContextMenu,
  onConnect: onConnectProp,
  onConnectEnd: onConnectEndProp,
  allowDragAndDrop = false,
  onDrop: onDropProp,
  onDragOver: onDragOverProp,
  validateConnection: validateConnectionProp,
  showControls = true,
  showMiniMap = true,
  showBackground = true,
  showDetailsPanel = true,
  hasUnsavedChanges: externalHasUnsavedChanges,
  isSaving = false,
  isLoading = false,
  detailsPanelComponent: DetailsPanelComponent,
  className = '',
  fitViewOnLoad = true,
  defaultViewport,
  onViewportChange,
  helperLinesEnabled: externalHelperLinesEnabled,
  onHelperLinesToggle,
  enableUndoRedo = false,
  children,
}: FlowCanvasProps) {
  const { toast } = useToast();
  const reactFlowInstance = useReactFlow();
  
  // Undo/Redo functionality - always call hook unconditionally
  const undoRedo = useUndoRedo({ 
    enableShortcuts: enableUndoRedo,
    maxHistorySize: 100 
  });
  
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [internalHasUnsavedChanges, setInternalHasUnsavedChanges] = useState(false);
  const [internalHelperLinesEnabled, setInternalHelperLinesEnabled] = useState(false);
  const [edgeContextMenu, setEdgeContextMenu] = useState<{
    edge: Edge | null;
    position: { x: number; y: number };
    isOpen: boolean;
  }>({ edge: null, position: { x: 0, y: 0 }, isOpen: false });
  
  // Store original state for change tracking
  const [originalNodes, setOriginalNodes] = useState<Node[]>(initialNodes);
  const [originalEdges, setOriginalEdges] = useState<Edge[]>(initialEdges);
  
  const hasLoadedRef = useRef(false);
  
  // Use external unsaved changes if provided, otherwise use internal tracking
  const hasUnsavedChanges = externalHasUnsavedChanges !== undefined 
    ? externalHasUnsavedChanges 
    : internalHasUnsavedChanges;

  // Use external helper lines enabled if provided, otherwise use internal tracking
  const helperLinesEnabled = externalHelperLinesEnabled !== undefined 
    ? externalHelperLinesEnabled 
    : internalHelperLinesEnabled;

  // Handle helper lines toggle
  const handleHelperLinesToggle = useCallback((enabled: boolean) => {
    if (onHelperLinesToggle) {
      onHelperLinesToggle(enabled);
    } else {
      setInternalHelperLinesEnabled(enabled);
    }
  }, [onHelperLinesToggle]);

  // Handle edges deletion (called before deletion)
  const handleEdgesDelete = useCallback(() => {
    // Take snapshot before deletion (for undo/redo)
    if (enableUndoRedo) {
      undoRedo.takeSnapshot();
    }
  }, [enableUndoRedo, undoRedo]);

  // Handle node drag start (for undo/redo of position changes)
  const handleNodeDragStart = useCallback(() => {
    // Take snapshot before dragging (for undo/redo)
    if (enableUndoRedo) {
      undoRedo.takeSnapshot();
    }
  }, [enableUndoRedo, undoRedo]);

  // Handle selection drag start (for undo/redo of position changes)
  const handleSelectionDragStart = useCallback(() => {
    // Take snapshot before dragging selection (for undo/redo)
    if (enableUndoRedo) {
      undoRedo.takeSnapshot();
    }
  }, [enableUndoRedo, undoRedo]);

  // Handle node drag with snapping when helper lines are enabled
  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    if (helperLinesEnabled) {
      // Apply snapping to position changes
      const snappedChanges = changes.map((change) => {
        if (change.type === 'position' && change.dragging && change.position) {
          // Find the node being dragged
          const node = nodes.find((n) => n.id === change.id);
          if (node) {
            const nodeWidth = (node.measured?.width || node.width) ?? 150;
            const nodeHeight = (node.measured?.height || node.height) ?? 40;
            
            // Create a temporary node with the new position
            const tempNode = { ...node, position: change.position };
            
            // Calculate helper lines and snap position
            const result = getHelperLines(tempNode, nodes, nodeWidth, nodeHeight);
            
            // Apply snap position
            return {
              ...change,
              position: result.snapPosition,
            };
          }
        }
        return change;
      });
      
      onNodesChange(snappedChanges);
    } else {
      onNodesChange(changes);
    }
  }, [helperLinesEnabled, nodes, onNodesChange]);

  // Update nodes and edges when initialNodes/initialEdges change
  // Note: Edge handlers are injected later after edges are set
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    setOriginalNodes(initialNodes);
    setOriginalEdges(initialEdges);
    hasLoadedRef.current = false;
    
    // Defer hasLoadedRef to allow initial render
    setTimeout(() => {
      hasLoadedRef.current = true;
    }, 100);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Track changes when nodes or edges update
  useEffect(() => {
    if (hasLoadedRef.current && externalHasUnsavedChanges === undefined) {
      const nodesChanged = hasNodeChanges(nodes, originalNodes);
      const edgesChanged = hasEdgeChanges(edges, originalEdges);
      setInternalHasUnsavedChanges(nodesChanged || edgesChanged);
    }
  }, [nodes, edges, originalNodes, originalEdges, externalHasUnsavedChanges]);

  // Fit view on load if requested
  useEffect(() => {
    if (fitViewOnLoad && reactFlowInstance && nodes.length > 0 && !defaultViewport) {
      // Delay to ensure nodes are rendered with their dimensions
      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.2,
          duration: 200,
        });
      }, 50);
    }
  }, [fitViewOnLoad, reactFlowInstance, nodes.length, defaultViewport]);

  // Notify parent of changes
  useEffect(() => {
    if (hasLoadedRef.current) {
      // Skip calling onNodesChangeProp if we're restoring from undo/redo
      // This prevents the dialog flow from updating its sections data during restore
      if (enableUndoRedo && undoRedo.isRestoring()) {
        return;
      }
      
      onNodesChangeProp?.(nodes);
    }
  }, [nodes, onNodesChangeProp, enableUndoRedo, undoRedo]);

  useEffect(() => {
    if (hasLoadedRef.current) {
      // Skip calling onEdgesChangeProp if we're restoring from undo/redo
      // This prevents the dialog flow from updating its sections data during restore
      if (enableUndoRedo && undoRedo.isRestoring()) {
        return;
      }
      
      onEdgesChangeProp?.(edges);
    }
  }, [edges, onEdgesChangeProp, enableUndoRedo, undoRedo]);

  // Handle drag over
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    onDragOverProp?.(event);
  }, [onDragOverProp]);

  // Handle drop
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (!onDropProp || !reactFlowInstance) return;

      const newNode = onDropProp(event, nodes);
      if (newNode) {
        // Note: We don't take snapshots for adding nodes because the dialog flow
        // has its own data layer (sections) that would need to be included
        
        // Calculate position based on mouse location
        const position = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
        
        setNodes((nds) => [...nds, { ...newNode, position }]);
      }
    },
    [onDropProp, nodes, setNodes, reactFlowInstance]
  );

  // Handle connection with validation
  const onConnect = useCallback(
    (params: Connection) => {
      // Custom validation if provided
      if (validateConnectionProp) {
        const result = validateConnectionProp(params);
        const isValid = typeof result === 'boolean' ? result : result.valid;
        const message = typeof result === 'object' ? result.message : undefined;
        
        if (!isValid) {
          toast({
            title: 'Invalid connection',
            description: message || 'This connection is not allowed',
            variant: 'destructive',
          });
          return;
        }
      } else {
        // Default handle validation
        const sourceHandle = params.sourceHandle;
        const targetHandle = params.targetHandle;
        
        if (!validateHandleConnection(sourceHandle, targetHandle)) {
          toast({
            title: 'Invalid connection',
            description: `Cannot connect ${sourceHandle} to ${targetHandle}. Connect opposite handles (e.g., right to left, bottom to top).`,
            variant: 'destructive',
          });
          return;
        }
      }

      // Note: We don't take snapshots for connections because the dialog flow
      // has its own data layer (sections) that would need to be included

      // Call custom onConnect handler if provided (for data updates)
      if (onConnectProp) {
        onConnectProp(params);
      }

      const newEdge: Edge = {
        ...params,
        id: `edge-${Date.now()}`,
        type: edgeTypes ? 'default' : 'default',
        data: {
          label: '',
        },
      } as Edge;

      setEdges((eds) => addEdge(newEdge, eds));
    },
    [validateConnectionProp, onConnectProp, edgeTypes, setEdges, toast]
  );

  const onConnectEnd = useCallback(
    (event: any, connectionState: any) => {
      if (!onConnectEndProp) return;

      const flowPosition = event?.clientX != null && event?.clientY != null
        ? reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          })
        : undefined;

      onConnectEndProp(event, connectionState, flowPosition);
    },
    [onConnectEndProp, reactFlowInstance]
  );

  // Handle pane context menu with flow position
  const handlePaneContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (!onPaneContextMenu) return;

      const flowPosition = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      onPaneContextMenu(event, flowPosition);
    },
    [onPaneContextMenu, reactFlowInstance]
  );

  // Handle node selection
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
    setEdgeContextMenu({ edge: null, position: { x: 0, y: 0 }, isOpen: false });
    if (showDetailsPanel) {
      setIsPanelOpen(true);
    }
    onNodeClickProp?.(node);
  }, [showDetailsPanel, onNodeClickProp]);

  // Handle edge selection
  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    setEdgeContextMenu({ edge: null, position: { x: 0, y: 0 }, isOpen: false });
    if (showDetailsPanel) {
      setIsPanelOpen(true);
    }
    onEdgeClickProp?.(edge);
  }, [showDetailsPanel, onEdgeClickProp]);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setEdgeContextMenu({ edge: null, position: { x: 0, y: 0 }, isOpen: false });
    onPaneClickProp?.();
  }, [onPaneClickProp]);

  // Delete node
  const handleDeleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    setIsPanelOpen(false);
  }, [setNodes, setEdges]);

  // Delete edge
  const handleDeleteEdge = useCallback((edgeId: string) => {
    // Take snapshot before deletion (for undo/redo)
    if (enableUndoRedo) {
      undoRedo.takeSnapshot();
    }
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    setSelectedEdge(null);
    setIsPanelOpen(false);
  }, [setEdges, enableUndoRedo, undoRedo]);

  // Update edge label
  const handleUpdateEdgeLabel = useCallback((edgeId: string, label: string) => {
    setEdges((eds) =>
      eds.map((e) =>
        e.id === edgeId
          ? { ...e, data: { ...e.data, label } }
          : e
      )
    );
  }, [setEdges]);

  // Handle node double click
  const handleNodeDoubleClick = useCallback((_event: React.MouseEvent, node: Node) => {
    onNodeDoubleClick?.(node);
  }, [onNodeDoubleClick]);

  // Handle edge double click
  const handleEdgeDoubleClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    onEdgeDoubleClick?.(edge);
  }, [onEdgeDoubleClick]);

  // Handle edge context menu (right-click)
  const handleEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.preventDefault();
    setEdgeContextMenu({
      edge,
      position: { x: event.clientX, y: event.clientY },
      isOpen: true,
    });
  }, []);

  // Memoize edge handlers for injection into edge data
  const edgeHandlers = useMemo(() => ({
    onEdgeContextMenu: handleEdgeContextMenu,
    onDeleteEdge: handleDeleteEdge,
  }), [handleEdgeContextMenu, handleDeleteEdge]);

  // Inject handlers into edges when they change or when handlers change
  useEffect(() => {
    setEdges((eds) => {
      // Check if any edge is missing handlers to avoid unnecessary updates
      const needsUpdate = eds.some(
        (e) => e.data?.onEdgeContextMenu !== handleEdgeContextMenu || 
               e.data?.onDeleteEdge !== handleDeleteEdge
      );
      
      if (!needsUpdate) return eds;
      
      return eds.map((edge) => ({
        ...edge,
        data: {
          ...edge.data,
          onEdgeContextMenu: handleEdgeContextMenu,
          onDeleteEdge: handleDeleteEdge,
        },
      }));
    });
  }, [edges, handleEdgeContextMenu, handleDeleteEdge, setEdges]);

  // Handle edge style change
  const handleChangeEdgeStyle = useCallback((edgeId: string, newType: string) => {
    setEdges((eds) =>
      eds.map((e) =>
        e.id === edgeId
          ? { 
              ...e, 
              type: newType,
              // Preserve animated state and handlers
              animated: e.animated,
              data: {
                ...e.data,
                ...edgeHandlers,
              },
            }
          : e
      )
    );
  }, [setEdges, edgeHandlers]);

  // Handle toggle edge animated
  const handleToggleEdgeAnimated = useCallback((edgeId: string) => {
    setEdges((eds) =>
      eds.map((e) =>
        e.id === edgeId
          ? { 
              ...e, 
              animated: !e.animated,
              data: {
                ...e.data,
                ...edgeHandlers,
              },
            }
          : e
      )
    );
  }, [setEdges, edgeHandlers]);

  // Close edge context menu
  const handleCloseEdgeContextMenu = useCallback(() => {
    setEdgeContextMenu({ edge: null, position: { x: 0, y: 0 }, isOpen: false });
  }, []);

  // Handle viewport changes (zoom and pan)
  const handleMove = useCallback((_event: any, viewport: { x: number; y: number; zoom: number }) => {
    onViewportChange?.(viewport);
  }, [onViewportChange]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Loading flow...</p>
        </div>
      </div>
    );
  }

  const DetailsPanelToRender = DetailsPanelComponent || NodeDetailsPanel;

  return (
    <div className={`w-full h-full ${className}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        deleteKeyCode={['Backspace', 'Delete']}
        selectionOnDrag={true}
        selectionKeyCode="Shift"
        panOnDrag={true}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeDoubleClick={handleEdgeDoubleClick}
        onEdgeContextMenu={handleEdgeContextMenu}
        onDrop={allowDragAndDrop ? onDrop : undefined}
        onDragOver={allowDragAndDrop ? onDragOver : undefined}
        onConnectEnd={onConnectEndProp ? (onConnectEnd as OnConnectEnd) : undefined}
        onPaneContextMenu={handlePaneContextMenu}
        onMove={handleMove}
        onNodeDragStart={enableUndoRedo ? handleNodeDragStart : undefined}
        onSelectionDragStart={enableUndoRedo ? handleSelectionDragStart : undefined}
        onEdgesDelete={enableUndoRedo ? handleEdgesDelete : undefined}
        defaultViewport={defaultViewport}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        minZoom={0.1}
        maxZoom={2}
        className="bg-background"
        proOptions={{ hideAttribution: true }}
      >
        {showBackground && <Background />}
        {showControls && <Controls />}
        {showMiniMap && <MiniMap />}
        {helperLinesEnabled && <HelperLines />}
        
        {/* Undo/Redo Controls */}
        {/* {enableUndoRedo && undoRedo && (
          <Panel position="bottom-left" className="m-2">
            <div className="flex gap-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-1">
              <button
                onClick={undoRedo.undo}
                disabled={!undoRedo.canUndo}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Undo (Ctrl+Z)"
              >
                <Undo2 className="w-4 h-4 text-gray-700 dark:text-gray-300" />
              </button>
              <button
                onClick={undoRedo.redo}
                disabled={!undoRedo.canRedo}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Redo (Ctrl+Y)"
              >
                <Redo2 className="w-4 h-4 text-gray-700 dark:text-gray-300" />
              </button>
            </div>
          </Panel>
        )} */}
        
        {children}
      </ReactFlow>

      {/* Details Panel */}
      {showDetailsPanel && isPanelOpen && (
        <DetailsPanelToRender
          selectedNode={selectedNode}
          selectedEdge={selectedEdge}
          onDeleteNode={handleDeleteNode}
          onDeleteEdge={handleDeleteEdge}
          onUpdateEdgeLabel={handleUpdateEdgeLabel}
          onClose={() => setIsPanelOpen(false)}
        />
      )}

      {/* Edge Style Context Menu */}
      <EdgeStyleContextMenu
        edge={edgeContextMenu.edge}
        position={edgeContextMenu.position}
        isOpen={edgeContextMenu.isOpen}
        onClose={handleCloseEdgeContextMenu}
        onChangeStyle={handleChangeEdgeStyle}
        onToggleAnimated={handleToggleEdgeAnimated}
      />
    </div>
  );
}

/**
 * FlowCanvas - Main export with ReactFlowProvider wrapper
 */
export function FlowCanvas(props: FlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvasContent {...props} />
    </ReactFlowProvider>
  );
}

export default FlowCanvas;

